import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { PageHeader } from "../components/PageHeader";
import {
  getBrokerLink,
  getMirrorHistory,
  getBrokerDisplayName,
  getLinkStatusDisplay,
  getMirrorQueueStatusDisplay,
  type BrokerLinkSummary,
  type MirrorQueueEntry,
  type MirrorQueueStatus,
} from "../services/brokerMirrorService";
import { getBrokerageAccount, type BrokerageAccount } from "../services/brokerageService";

export function MirrorDashboardPage() {
  const { accountId, linkId } = useParams<{ accountId: string; linkId: string }>();

  const [account, setAccount] = useState<BrokerageAccount | null>(null);
  const [link, setLink] = useState<BrokerLinkSummary | null>(null);
  const [entries, setEntries] = useState<MirrorQueueEntry[]>([]);
  const [totalEntries, setTotalEntries] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<MirrorQueueStatus | "">("");
  const [symbolFilter, setSymbolFilter] = useState("");
  const [sideFilter, setSideFilter] = useState<"" | "buy" | "sell">("");
  const limit = 500;
  const [sortField, setSortField] = useState<string>("created_at");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

  const sortedEntries = useMemo(() => {
    const sorted = [...entries];
    sorted.sort((a, b) => {
      let aVal: string | number = "";
      let bVal: string | number = "";
      switch (sortField) {
        case "symbol": aVal = a.symbol; bVal = b.symbol; break;
        case "side": aVal = a.side; bVal = b.side; break;
        case "qty": aVal = parseFloat(a.qty) || 0; bVal = parseFloat(b.qty) || 0; break;
        case "broker_fill_qty": aVal = parseFloat(a.broker_fill_qty || "0"); bVal = parseFloat(b.broker_fill_qty || "0"); break;
        case "alpaca_fill_price": aVal = parseFloat(a.alpaca_fill_price) || 0; bVal = parseFloat(b.alpaca_fill_price) || 0; break;
        case "broker_fill_price": aVal = parseFloat(a.broker_fill_price || "0"); bVal = parseFloat(b.broker_fill_price || "0"); break;
        case "status": aVal = a.status; bVal = b.status; break;
        case "created_at": aVal = a.completed_at || a.created_at; bVal = b.completed_at || b.created_at; break;
        case "attempts": aVal = a.attempts; bVal = b.attempts; break;
      }
      if (aVal < bVal) return sortDir === "asc" ? -1 : 1;
      if (aVal > bVal) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [entries, sortField, sortDir]);

  function handleSort(field: string) {
    if (sortField === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  }

  function sortIndicator(field: string) {
    if (sortField !== field) return " ↕";
    return sortDir === "asc" ? " ↑" : " ↓";
  }

  const loadData = useCallback(async () => {
    if (!accountId || !linkId) return;
    try {
      setLoading(true);
      const [acct, linkData, history] = await Promise.all([
        getBrokerageAccount(accountId),
        getBrokerLink(linkId),
        getMirrorHistory(linkId, {
          page,
          limit,
          status: statusFilter || undefined,
          symbol: symbolFilter || undefined,
          side: sideFilter || undefined,
        }),
      ]);
      setAccount(acct);
      setLink(linkData);
      setEntries(history.entries || []);
      setTotalEntries(history.total);
      setTotalPages(history.pages);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load data");
    } finally {
      setLoading(false);
    }
  }, [accountId, linkId, page, statusFilter, symbolFilter, sideFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [statusFilter, symbolFilter, sideFilter]);

  function formatDateTime(dateStr: string | undefined): string {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  function formatPrice(value: string | undefined): string {
    if (!value) return "—";
    const num = parseFloat(value);
    return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(num);
  }

  const brokerName = link ? getBrokerDisplayName(link.broker_type) : "Broker";

  return (
    <div>
      <PageHeader
        title={`Mirror History — ${brokerName}`}
        subtitle={account ? `Trades mirrored from "${account.account_name}"` : undefined}
        backgroundImage="https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1920&q=80"
      />

      <div className="px-4 sm:px-6 lg:px-8 pb-8">
        {/* Breadcrumb */}
        <nav className="flex flex-wrap items-center gap-2 text-sm text-surface-400 mb-6">
          <Link to="/brokerage" className="hover:text-primary-400 transition-colors">Brokerage</Link>
          <svg className="h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
          <Link to={`/brokerage/${accountId}/mirror`} className="hover:text-primary-400 transition-colors">{account?.account_name || "Account"}</Link>
          <svg className="h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
          <span className="text-surface-200">{brokerName} History</span>
        </nav>

        {error && (
          <div className="mb-6 glass-card bg-red-500/10 border-red-500/30 text-red-400 px-4 py-3 text-sm">
            {error}
          </div>
        )}

        {loading && !link ? (
          <div className="flex items-center justify-center py-20">
            <div className="flex flex-col items-center gap-4">
              <div className="spinner w-8 h-8" />
              <p className="text-surface-400">Loading mirror history...</p>
            </div>
          </div>
        ) : link ? (
          <>
            {/* Summary cards */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
              <div className="glass-card p-4">
                <p className="text-xs text-surface-500 mb-1">Status</p>
                <span className={`px-2 py-1 text-xs font-medium rounded ${getLinkStatusDisplay(link.status).colorClass}`}>
                  {getLinkStatusDisplay(link.status).label}
                </span>
              </div>
              <div className="glass-card p-4">
                <p className="text-xs text-surface-500 mb-1">Today Mirrored</p>
                <p className="text-xl font-semibold text-surface-100">{link.today_mirrored}</p>
              </div>
              <div className="glass-card p-4">
                <p className="text-xs text-surface-500 mb-1">Completed</p>
                <p className="text-xl font-semibold text-accent-400">{link.today_completed}</p>
              </div>
              <div className="glass-card p-4">
                <p className="text-xs text-surface-500 mb-1">Failed</p>
                <p className="text-xl font-semibold text-red-400">{link.today_failed}</p>
              </div>
            </div>

            {/* Last error banner */}
            {link.last_mirror_error && (
              <div className="mb-6 glass-card bg-red-500/10 border-red-500/30 px-4 py-3">
                <p className="text-xs text-surface-500 mb-1">Last Error</p>
                <p className="text-sm text-red-400">{link.last_mirror_error}</p>
              </div>
            )}

            {/* Filters */}
            <div className="mb-4 flex flex-col sm:flex-row gap-3">
              <div className="flex-1 sm:max-w-xs">
                <input
                  type="text"
                  value={symbolFilter}
                  onChange={(e) => setSymbolFilter(e.target.value.toUpperCase())}
                  placeholder="Filter by symbol..."
                  className="w-full px-3 py-2 bg-surface-800 border border-surface-600 rounded-lg text-surface-100 placeholder-surface-500 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors text-sm"
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as MirrorQueueStatus | "")}
                className="px-3 py-2 bg-surface-800 border border-surface-600 rounded-lg text-surface-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors text-sm"
              >
                <option value="">All statuses</option>
                <option value="pending">Pending</option>
                <option value="processing">Processing</option>
                <option value="completed">Completed</option>
                <option value="failed">Failed</option>
                <option value="skipped">Skipped</option>
              </select>
              <select
                value={sideFilter}
                onChange={(e) => setSideFilter(e.target.value as "" | "buy" | "sell")}
                className="px-3 py-2 bg-surface-800 border border-surface-600 rounded-lg text-surface-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors text-sm"
              >
                <option value="">All sides</option>
                <option value="buy">Buy</option>
                <option value="sell">Sell</option>
              </select>
              <button
                onClick={loadData}
                disabled={loading}
                className="btn-secondary text-sm"
              >
                {loading ? "Loading..." : "Refresh"}
              </button>
            </div>

            {/* Mirror history table */}
            {entries.length === 0 ? (
              <div className="glass-card p-8 text-center">
                <p className="text-surface-400">No mirror entries found{statusFilter || symbolFilter ? " for the selected filters" : " yet"}.</p>
              </div>
            ) : (
              <>
                <p className="text-sm text-surface-400 mb-2">{totalEntries} entries</p>
                {/* Desktop table */}
                <div className="hidden md:block glass-card overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-surface-700/50">
                          <th className="text-left px-4 py-3 text-xs font-medium text-surface-400 uppercase tracking-wider cursor-pointer hover:text-surface-200 select-none" onClick={() => handleSort("symbol")}>Symbol{sortIndicator("symbol")}</th>
                          <th className="text-left px-4 py-3 text-xs font-medium text-surface-400 uppercase tracking-wider cursor-pointer hover:text-surface-200 select-none" onClick={() => handleSort("side")}>Side{sortIndicator("side")}</th>
                          <th className="text-right px-4 py-3 text-xs font-medium text-surface-400 uppercase tracking-wider cursor-pointer hover:text-surface-200 select-none" onClick={() => handleSort("qty")}>Qty{sortIndicator("qty")}</th>
                          <th className="text-right px-4 py-3 text-xs font-medium text-surface-400 uppercase tracking-wider cursor-pointer hover:text-surface-200 select-none" onClick={() => handleSort("broker_fill_qty")}>Broker Qty{sortIndicator("broker_fill_qty")}</th>
                          <th className="text-right px-4 py-3 text-xs font-medium text-surface-400 uppercase tracking-wider cursor-pointer hover:text-surface-200 select-none" onClick={() => handleSort("alpaca_fill_price")}>Alpaca Price{sortIndicator("alpaca_fill_price")}</th>
                          <th className="text-right px-4 py-3 text-xs font-medium text-surface-400 uppercase tracking-wider cursor-pointer hover:text-surface-200 select-none" onClick={() => handleSort("broker_fill_price")}>Broker Price{sortIndicator("broker_fill_price")}</th>
                          <th className="text-center px-4 py-3 text-xs font-medium text-surface-400 uppercase tracking-wider cursor-pointer hover:text-surface-200 select-none" onClick={() => handleSort("status")}>Status{sortIndicator("status")}</th>
                          <th className="text-left px-4 py-3 text-xs font-medium text-surface-400 uppercase tracking-wider cursor-pointer hover:text-surface-200 select-none" onClick={() => handleSort("created_at")}>Time{sortIndicator("created_at")}</th>
                          <th className="text-right px-4 py-3 text-xs font-medium text-surface-400 uppercase tracking-wider cursor-pointer hover:text-surface-200 select-none" onClick={() => handleSort("attempts")}>Attempts{sortIndicator("attempts")}</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-surface-700/30">
                        {sortedEntries.map((entry) => {
                          const statusDisplay = getMirrorQueueStatusDisplay(entry.status);
                          return (
                            <React.Fragment key={entry.id}>
                              <tr className="hover:bg-surface-800/30 transition-colors">
                                <td className="px-4 py-3 font-medium text-surface-100">{entry.symbol}</td>
                                <td className="px-4 py-3">
                                  <span className={entry.side === "buy" ? "text-accent-400" : "text-red-400"}>
                                    {entry.side.toUpperCase()}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-right text-surface-200">{entry.qty}</td>
                                <td className="px-4 py-3 text-right text-surface-200">{entry.broker_fill_qty ? Math.floor(parseFloat(entry.broker_fill_qty)) : "—"}</td>
                                <td className="px-4 py-3 text-right text-surface-200">{formatPrice(entry.alpaca_fill_price)}</td>
                                <td className="px-4 py-3 text-right text-surface-200">{formatPrice(entry.broker_fill_price)}</td>
                                <td className="px-4 py-3 text-center">
                                  <span className={`px-2 py-0.5 text-xs font-medium rounded ${statusDisplay.colorClass}`}>
                                    {statusDisplay.label}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-surface-400">{formatDateTime(entry.completed_at || entry.created_at)}</td>
                                <td className="px-4 py-3 text-right text-surface-400">{entry.attempts}/{entry.max_attempts}</td>
                              </tr>
                              {entry.error_message && (entry.status === "failed" || entry.status === "skipped") && (
                                <tr className={entry.status === "skipped" ? "bg-yellow-900/10" : "bg-red-900/10"}>
                                  <td colSpan={9} className={`px-4 py-1.5 text-xs ${entry.status === "skipped" ? "text-yellow-400" : "text-red-400"}`}>
                                    <span className={`font-medium ${entry.status === "skipped" ? "text-yellow-500/70" : "text-red-500/70"}`}>{entry.status === "skipped" ? "Skipped:" : "Error:"}</span> {entry.error_message}
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Mobile card list */}
                <div className="md:hidden space-y-3">
                  {entries.map((entry) => {
                    const statusDisplay = getMirrorQueueStatusDisplay(entry.status);
                    return (
                      <div key={entry.id} className="glass-card p-4">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-surface-100">{entry.symbol}</span>
                            <span className={entry.side === "buy" ? "text-accent-400 text-xs font-medium" : "text-red-400 text-xs font-medium"}>
                              {entry.side.toUpperCase()}
                            </span>
                          </div>
                          <span className={`px-2 py-0.5 text-xs font-medium rounded ${statusDisplay.colorClass}`}>
                            {statusDisplay.label}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          <div>
                            <span className="text-surface-500">Qty:</span>{" "}
                            <span className="text-surface-200">{entry.qty}</span>
                          </div>
                          <div>
                            <span className="text-surface-500">Attempts:</span>{" "}
                            <span className="text-surface-200">{entry.attempts}/{entry.max_attempts}</span>
                          </div>
                          <div>
                            <span className="text-surface-500">Alpaca:</span>{" "}
                            <span className="text-surface-200">{formatPrice(entry.alpaca_fill_price)}</span>
                          </div>
                          <div>
                            <span className="text-surface-500">Broker:</span>{" "}
                            <span className="text-surface-200">{formatPrice(entry.broker_fill_price)}</span>
                          </div>
                        </div>
                        {entry.error_message && (
                          <p className="mt-2 text-xs text-red-400 truncate" title={entry.error_message}>
                            {entry.error_message}
                          </p>
                        )}
                        <p className="mt-2 text-xs text-surface-500">
                          {formatDateTime(entry.completed_at || entry.created_at)}
                        </p>
                      </div>
                    );
                  })}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="mt-4 flex flex-col sm:flex-row items-center justify-between gap-3">
                    <p className="text-sm text-surface-400">
                      Showing {(page - 1) * limit + 1}–{Math.min(page * limit, totalEntries)} of {totalEntries}
                    </p>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        disabled={page <= 1}
                        className="px-3 py-1.5 text-sm bg-surface-800 border border-surface-600 rounded-lg text-surface-300 hover:bg-surface-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        Previous
                      </button>
                      <span className="px-3 py-1.5 text-sm text-surface-400">
                        {page} / {totalPages}
                      </span>
                      <button
                        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                        disabled={page >= totalPages}
                        className="px-3 py-1.5 text-sm bg-surface-800 border border-surface-600 rounded-lg text-surface-300 hover:bg-surface-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        ) : null}
      </div>
    </div>
  );
}
